import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';

/**
 * Top banner: greeting + long date + up to 3 KPI chips.
 *
 * KPI keys are resolved here so each role's layout just declares the KPI
 * keys it wants. Adding a new KPI = register it in KPI_LOADERS.
 *
 * Every role gets "tasks_assigned" by default. Driver role overrides with
 * { tasks_assigned, space_calls_queue, avg_resolution }.
 */

// KPI loader registry. Each returns { value, label, href? }. Async.
const KPI_LOADERS = {
  tasks_assigned: async () => {
    try {
      const d = await api.getWorkflowItemCounts();
      return { value: d?.active ?? 0, label: 'Tasks Assigned', href: '/workflow/my-work' };
    } catch { return { value: '—', label: 'Tasks Assigned', href: '/workflow/my-work' }; }
  },
  space_calls_queue: async () => {
    try {
      const d = await api.getDashboardQueueStats();
      return { value: d?.space_calls ?? 0, label: 'Space Calls', href: '/queue' };
    } catch { return { value: '—', label: 'Space Calls', href: '/queue' }; }
  },
  avg_resolution: async () => {
    try {
      const d = await api.getDashboardDriverStats();
      const mins = d?.avg_resolution_minutes;
      return { value: mins == null ? '—' : `${mins}m`, label: 'Avg Resolution' };
    } catch { return { value: '—', label: 'Avg Resolution' }; }
  },
  placeholder: async () => ({ value: '+', label: 'Coming soon', isPlaceholder: true }),
};

function formatLongDate(now = new Date()) {
  // "Monday, April 21" — no year, matches Matt's spec.
  return now.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

/**
 * Avatar — photo_url if present, otherwise a colored initials circle.
 * Matches the admin pages' Avatar pattern so the dashboard looks
 * consistent with the rest of the app.
 */
function Avatar({ user, size = 56 }) {
  const hasPhoto = !!user?.photo_url;
  const name = user?.full_name || user?.username || '';
  if (hasPhoto) {
    return (
      <img
        src={user.photo_url}
        alt={name}
        className="dash-welcome-avatar"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="dash-welcome-avatar dash-welcome-avatar-initials"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
      aria-label={name}
    >
      {initials(name)}
    </div>
  );
}

export default function WelcomeBanner({ user, kpiKeys = ['tasks_assigned', 'placeholder', 'placeholder'] }) {
  const [kpis, setKpis] = useState([]);

  const kpiKeysStr = kpiKeys.join(',');
  useEffect(() => {
    let cancelled = false;
    const keys = kpiKeysStr.split(',');
    Promise.all(keys.map(k => (KPI_LOADERS[k] || KPI_LOADERS.placeholder)())).then(results => {
      if (!cancelled) setKpis(results);
    });
    return () => { cancelled = true; };
  }, [kpiKeysStr]);

  const firstName = (user?.full_name || user?.username || '').split(' ')[0] || 'there';

  return (
    <header className="dash-welcome">
      <div className="dash-welcome-identity">
        <Avatar user={user} />
        <div>
          <h1 className="dash-welcome-greeting">Welcome back, {firstName}</h1>
          <p className="dash-welcome-date">{formatLongDate()}</p>
        </div>
      </div>
      <div className="dash-welcome-kpis">
        {kpis.map((kpi, i) => {
          const placeholderClass = kpi.isPlaceholder ? ' dash-welcome-kpi-placeholder' : '';
          const content = (
            <>
              <div className="dash-welcome-kpi-value">{kpi.value}</div>
              <div className="dash-welcome-kpi-label">{kpi.label}</div>
            </>
          );
          return kpi.href ? (
            <Link key={i} to={kpi.href} className={`dash-welcome-kpi${placeholderClass}`} role="link">
              {content}
            </Link>
          ) : (
            <div key={i} className={`dash-welcome-kpi${placeholderClass}`}>{content}</div>
          );
        })}
      </div>
    </header>
  );
}
